import { pxToRem } from "@input-output-hk/px-to-rem";
import { normalizeSlug } from "@input-output-hk/essential-cardano-utils";
import { useField, useFormikContext } from "formik";
import React from "react";
import styled from "styled-components";
import { trueOrUndef } from "../../utils/dom-attribute-helpers";
import Button from "../buttons/Button";
import Input from "../input/Input";
import { FormFieldElement } from "./FormFieldElement";
import { FormFieldLabel } from "./FormFieldLabel";

const SlugInputRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-default);
`;

/**
 * The error message renders inside `Input`, so the input needs to
 * own the space underneath it
 */
const SlugInputWrapper = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
`;

const GenerateButton = styled(Button)`
  flex: 0 0 auto;
  /* Line the button up with the input, not with its error message */
  height: ${pxToRem(48)};
  min-width: auto;
`;

type Props = {
  label: string;
  name: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
};

/**
 * A text field for the published content URL, with a button that
 * generates the slug from the title i.e.
 * "How to join a stake pool" -> "how-to-join-a-stake-pool"
 */
const SlugField: React.FC<Props> = ({
  label,
  name,
  id = name,
  placeholder,
  disabled
}) => {
  const [field, meta, helpers] = useField<string>(name);
  const { values } = useFormikContext<{ title?: string }>();
  const showError = meta.touched && !!meta.error;
  const generatedSlug = normalizeSlug(values.title);

  return (
    <FormFieldElement>
      <FormFieldLabel htmlFor={id}>{label}</FormFieldLabel>
      <SlugInputRow>
        <SlugInputWrapper>
          <Input
            {...field}
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            data-error={trueOrUndef(showError)}
            aria-invalid={trueOrUndef(showError)}
            aria-errormessage={showError ? meta.error : undefined}
          />
        </SlugInputWrapper>
        <GenerateButton
          type="button"
          appearance="secondary"
          size="small"
          disabled={disabled || !generatedSlug}
          onClick={() => {
            helpers.setValue(generatedSlug);
            helpers.setTouched(true, false);
          }}
        >
          Generate from title
        </GenerateButton>
      </SlugInputRow>
    </FormFieldElement>
  );
};

export default SlugField;
